import * as v from 'valibot'

/**
 * Jobs — the work the studio does: for a client, staffed with players,
 * driven through a guarded status machine.
 *
 * The status vocabulary, and what each word means:
 *
 *   draft         being shaped; not promised to anyone yet
 *   published     visible to assigned players, waiting to start
 *   in_progress   the work is live
 *   on_hold       paused, deliberately
 *   completed     done — refused while any assignment is still at work
 *   cancelled     withdrawn; a cancelled job is dead, not restorable
 *
 * Everything on a job is editable in place for as long as it runs — the
 * whole point of mid-edit. The dates come in three pairs: the window
 * promised to the client (estimated), the confirmed schedule (start/end),
 * and the truth the status machine stamps (actual_start on first start,
 * actual_end on completion) — staff never type the actuals by hand.
 *
 * Money on an assignment is an integer of minor units (cents), so no float
 * ever touches an amount. The payment ledger of Phase D is the money truth;
 * these fields are the working record staff see and set.
 */

export const JOB_STATUSES = ['draft', 'published', 'in_progress', 'on_hold', 'completed', 'removed', 'cancelled'] as const
export type JobStatus = (typeof JOB_STATUSES)[number]

/** The hours the studio logged on one of its days (Dhaka), for the main page's month. */
export interface StudioHoursDay {
  /** YYYY-MM-DD in the studio's time. */
  day: string
  hours: number
}

/** A month of logged hours, and the month before's total to measure it against. */
export interface StudioHoursMonth {
  /** YYYY-MM. */
  month: string
  days: StudioHoursDay[]
  previousHours: number
}

/**
 * How a job's status may follow another. The map is the whole machine, and
 * it lives here — the contract layer — so the API that refuses an illegal
 * move and the console that offers the legal ones can never disagree:
 *
 *   draft → published          the administrator releases prepared work
 *   published → draft          withdraw before any player has begun
 *   published → in_progress    work begins; actual_start is stamped once
 *   in_progress → on_hold      paused
 *   on_hold → in_progress      resumed
 *   in_progress → completed    every assignment must be at rest first
 *   in_progress → removed      the client pulled the work after it began;
 *                              like completed, the hours worked still count
 *                              and are paid — only the reason differs
 *   anything → cancelled       withdrawn from anywhere it is still open;
 *                              nothing on it is paid
 *   completed → in_progress    a regression brought the job back
 *   removed → in_progress      the client put the work back
 */
export const JOB_TRANSITIONS: Record<JobStatus, readonly JobStatus[]> = {
  draft: ['published', 'cancelled'],
  published: ['draft', 'in_progress', 'on_hold', 'cancelled'],
  in_progress: ['on_hold', 'completed', 'removed', 'cancelled'],
  on_hold: ['published', 'in_progress', 'removed', 'cancelled'],
  completed: ['in_progress'],
  removed: ['in_progress'],
  cancelled: [],
}

/**
 * What a player may do on a job in each state — the one table the portal's
 * verbs and the console's buttons both read. Hours and finishing need live
 * work (published or in progress).
 */
export const PLAYER_WORK_STATUSES: readonly JobStatus[] = ['published', 'in_progress']

/** Why a job's hours are closed, in the words a refusal and a row both use. */
export const lockedWorkReason = (title: string, status: JobStatus): string => {
  switch (status) {
    case 'on_hold': return `“${title}” is paused; its hours are locked until it resumes.`
    case 'completed': return `“${title}” is completed; its hours are locked.`
    case 'removed': return `“${title}” was removed; its hours are locked.`
    case 'cancelled': return `“${title}” was cancelled; its hours are locked.`
    default: return `“${title}” is a draft; publish it before logging work.`
  }
}

export const JOB_PRIORITIES = ['low', 'medium', 'high', 'critical'] as const
export type JobPriority = (typeof JOB_PRIORITIES)[number]

/**
 * An assignment's own lifecycle, separate from the job's: a player is
 * assigned, works, and ends completed, dropped (they stepped away), or
 * removed (staff took them off). A dropped or removed player can be brought
 * back — the row returns to `assigned` rather than being duplicated, because
 * one player sits on a job exactly once. A completion can be taken back too —
 * staff often mark hours on a player's behalf, and a mis-mark is a regression
 * to work, not a wound on the record.
 */
export const ASSIGNMENT_COMPLETIONS = ['assigned', 'in_progress', 'completed', 'dropped', 'removed'] as const
export type AssignmentCompletion = (typeof ASSIGNMENT_COMPLETIONS)[number]

export const COMPLETION_TRANSITIONS: Record<AssignmentCompletion, readonly AssignmentCompletion[]> = {
  assigned: ['in_progress', 'completed', 'dropped', 'removed'],
  in_progress: ['completed', 'dropped', 'removed'],
  completed: ['in_progress'],
  dropped: ['assigned'],
  removed: ['assigned'],
}

export const PAYMENT_STATUSES = ['unpaid', 'pending', 'paid'] as const
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number]

/**
 * How a rate is counted: by the hour the work consumes, or once for the
 * whole piece. The words the money section asks with at job creation.
 */
export const PAY_RATE_TYPES = ['hourly', 'one_time'] as const
export type PayRateType = (typeof PAY_RATE_TYPES)[number]

// ---- Notes -----------------------------------------------------------------

/** What a note may be pinned to. The API checks the entity exists. */
export const NOTE_ENTITY_TYPES = ['job', 'client', 'account', 'assignment'] as const
export type NoteEntityType = (typeof NOTE_ENTITY_TYPES)[number]

/**
 * `internal` never leaves staff; `shared` is what a player sees on the
 * portal. The word is the whole access decision, so it is a contract, not a
 * convention.
 */
export const NOTE_VISIBILITIES = ['internal', 'shared'] as const
export type NoteVisibility = (typeof NOTE_VISIBILITIES)[number]

// ---- The records ------------------------------------------------------------

/**
 * A kind of work. The studio's own vocabulary: administrators add, rename,
 * re-price, retire and restore these from Settings. A retired type keeps its
 * name on the work already under it and is offered to nothing new.
 */
export interface JobType {
  id: string
  name: string
  description: string | null
  /** The default hourly rate suggested for this kind of work, in minor units. */
  defaultRate: number | null
  isCustom: boolean
  /** The moment the type was retired, or null while it is offered to new work. */
  retiredAt: string | null
}

/**
 * One face on a job's row: who holds a part of it, how far along, in what
 * state. Dropped and removed hands are left off — the row shows who is on
 * the work now, and those who finished it.
 */
export interface JobCrewFace {
  assignmentId: string
  playerId: string
  name: string | null
  discordName: string | null
  email: string
  pictureUrl: string | null
  completion: AssignmentCompletion
  estimatedHours: number | null
  actualHours: number | null
}

export interface Job {
  id: string
  title: string
  description: string | null
  clientId: string | null
  /**
   * The client's name, joined for display — the API owns the join. When the
   * client was deleted, `clientId` is null and this is the name they had.
   */
  clientName: string | null
  /** The client was deleted; the job kept their name as history. */
  clientDeleted: boolean
  /** The client is archived — withdrawn from the working set, still on file. */
  clientArchived: boolean
  typeId: string
  typeName: string
  /**
   * The workflow that produced this job, when it was opened from a client's
   * template. Provenance only: the work's defaults were snapshotted onto the
   * job at birth, and the template's later edits never reach back here.
   */
  workflowId: string | null
  workflowName: string | null
  status: JobStatus
  /** Retained across withdrawal so releasing a job twice never announces it twice. */
  publishedAt: string | null
  archivedAt: string | null
  duplicatedFromJobId: string | null
  priority: JobPriority
  /** Stamped by the status machine, never typed by hand. */
  actualStart: string | null
  actualEnd: string | null
  /** How many players the work needs, as a range. */
  playersNeeded: number | null
  /**
   * A draft set to publish itself: the moment, and the administrator who set
   * it. The Worker's scheduled run publishes it then, as a press of Publish
   * would. Null for anything not waiting on the clock.
   */
  scheduledPublishAt: string | null
  /**
   * The hours the work needs — the pool the crew's allocations come out of.
   * Nullable: a job can be open-ended, its size known only in the crew's
   * sums.
   */
  estimatedHours: number | null
  /**
   * The pay terms, decided at creation and editable in place, nullable
   * because a job may exist before its money is decided. One currency for
   * the job (BDT by the console's prefilled hand); the client's rate and
   * the player's rate each carry their own basis. The two rates are
   * different secrets: the client's is the studio's margin and never
   * reaches a player; the player's is shown only when `playerRateVisible`
   * is spoken.
   */
  currency: string | null
  clientRateType: PayRateType | null
  clientRateAmount: number | null
  playerRateType: PayRateType | null
  playerRateAmount: number | null
  playerRateVisible: boolean
  /**
   * Whether the client's name travels with the work to the player's portal.
   * Off by default: the row says the work has a client without saying which,
   * until the studio speaks this word.
   */
  clientNameVisible: boolean
  /**
   * The hours story, summed by the API so every reader agrees:
   * `assignedHours` is what active allocations hold (assigned, working and
   * completed players; dropped and removed hands release theirs), and
   * `completedHours` is what finished work consumed — the hours actually
   * logged where they were, else the allocation that was marked done.
   */
  assignedHours: number
  completedHours: number
  /** Every hour logged so far by the crew on the work, finished or not — the bar's running count. */
  loggedHours: number
  /** The crew on the work, in the order they were assigned. */
  crew: JobCrewFace[]
  /**
   * Live work whose every player has finished or stepped away, with at
   * least one finished, and every finished part holding the hours it was
   * assigned: the job is waiting only for the studio to close it.
   * Summed by the API so the row's circle and the page agree.
   */
  readyToComplete: boolean
  /**
   * Every player has handed their part in, but at least one handed it in
   * with fewer hours than assigned: the work is in, the hours are not.
   */
  handedInShort: boolean
  notes: string | null
  internalNotes: string | null
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface JobAssignment {
  id: string
  jobId: string
  playerId: string
  playerName: string | null
  playerEmail: string
  playerDiscordName: string | null
  playerPictureUrl?: string | null
  assignedBy: string
  assignedAt: string
  /** In minor units. The ledger of Phase D is the money truth. */
  paymentAmount: number | null
  paymentStatus: PaymentStatus
  paymentDate: string | null
  completion: AssignmentCompletion
  completedAt: string | null
  /** Why work was dropped or a player removed — required for a drop. */
  droppedReason: string | null
  /** The studio's 1–5 for the work, and the words behind it. */
  quality: number | null
  qualityNotes: string | null
  estimatedHours: number | null
  actualHours: number | null
  notes: string | null
}

export interface JobNote {
  id: string
  entityType: NoteEntityType
  entityId: string
  authorId: string
  authorName: string | null
  body: string
  visibility: NoteVisibility
  createdAt: string
  updatedAt: string
}

// ---- Schemas -----------------------------------------------------------------

const required = (max: number) => v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(max))
/** Clearable text: a string to set it, null to clear it, absent to leave it. */
const nullableText = (max: number) => v.union([v.pipe(v.string(), v.trim(), v.maxLength(max)), v.null()])
/** A calendar date, and nothing more precise — schedule fields, not timestamps. */
const date = v.pipe(v.string(), v.trim(), v.regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD.'))
const dateOrNull = v.union([date, v.null()])
const idText = v.pipe(v.string(), v.trim(), v.minLength(1))
const players = v.pipe(v.number(), v.integer(), v.minValue(0), v.maxValue(500))
/** Money arrives as an integer of minor units; the console divides for display. */
const money = v.pipe(v.number(), v.integer(), v.minValue(0), v.maxValue(100_000_000))
const moneyOrNull = v.union([money, v.null()])
const hours = v.pipe(v.number(), v.minValue(0), v.maxValue(10_000))
const hoursOrNull = v.union([hours, v.null()])
/** A three-letter currency code, uppercased on arrival — BDT, USD, whatever the work is counted in. */
const currencyCode = v.pipe(
  v.string(),
  v.trim(),
  v.transform((value) => value.toUpperCase()),
  v.regex(/^[A-Z]{3}$/, 'Use a three-letter currency code, like BDT or USD.'),
)
const currencyOrNull = v.union([currencyCode, v.null()])
const payTypeOrNull = v.union([v.picklist(PAY_RATE_TYPES), v.null()])

const JOB_FIELDS = {
  title: required(160),
  clientId: v.optional(v.union([idText, v.null()])),
  typeId: v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(64)),
  description: v.optional(nullableText(8000)),
  priority: v.optional(v.picklist(JOB_PRIORITIES)),
  playersNeeded: v.optional(v.union([players, v.null()])),
  estimatedHours: v.optional(hoursOrNull),
  currency: v.optional(currencyOrNull),
  clientRateType: v.optional(payTypeOrNull),
  clientRateAmount: v.optional(moneyOrNull),
  playerRateType: v.optional(payTypeOrNull),
  playerRateAmount: v.optional(moneyOrNull),
  playerRateVisible: v.optional(v.boolean()),
  clientNameVisible: v.optional(v.boolean()),
  notes: v.optional(nullableText(5000)),
  internalNotes: v.optional(nullableText(5000)),
} as const


/**
 * The mid-edit contract, as everywhere: any subset of the record at any
 * time, absent means unchanged, null means cleared, at least one change is
 * required. Status is not here — moving a job is its own decision — and the
 * actual dates are not here because the status machine owns them.
 */
export const updateJobSchema = v.pipe(
  v.object({
    title: v.optional(required(160)),
    clientId: v.optional(v.union([idText, v.null()])),
    typeId: v.optional(v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(64))),
    description: v.optional(nullableText(8000)),
    priority: v.optional(v.picklist(JOB_PRIORITIES)),
    playersNeeded: v.optional(v.union([players, v.null()])),
    estimatedHours: v.optional(hoursOrNull),
    currency: v.optional(currencyOrNull),
    clientRateType: v.optional(payTypeOrNull),
    clientRateAmount: v.optional(moneyOrNull),
    playerRateType: v.optional(payTypeOrNull),
    playerRateAmount: v.optional(moneyOrNull),
    playerRateVisible: v.optional(v.boolean()),
    clientNameVisible: v.optional(v.boolean()),
    notes: v.optional(nullableText(5000)),
    internalNotes: v.optional(nullableText(5000)),
  }),
  v.check((input) => Object.values(input).some((value) => value !== undefined), 'Nothing to change.'),
)

export type UpdateJobInput = v.InferOutput<typeof updateJobSchema>

/** A new kind of work, minted by an administrator in Settings. */
export const createJobTypeSchema = v.object({
  name: required(80),
  description: v.optional(nullableText(500)),
  defaultRate: v.optional(moneyOrNull),
})

export type CreateJobTypeInput = v.InferOutput<typeof createJobTypeSchema>

/** The mid-edit contract, as everywhere: absent means unchanged. */
export const updateJobTypeSchema = v.pipe(
  v.object({
    name: v.optional(required(80)),
    description: v.optional(nullableText(500)),
    defaultRate: v.optional(moneyOrNull),
  }),
  v.check((input) => Object.values(input).some((value) => value !== undefined), 'Nothing to change.'),
)

export type UpdateJobTypeInput = v.InferOutput<typeof updateJobTypeSchema>

export const setJobStatusSchema = v.object({ status: v.picklist(JOB_STATUSES) })

/** When a draft publishes itself: an ISO moment, which the API also holds to the future. */
export const scheduleJobSchema = v.object({
  at: v.pipe(v.string(), v.trim(), v.isoTimestamp('Give the moment as a date and time.')),
})

export type ScheduleJobInput = v.InferOutput<typeof scheduleJobSchema>

/**
 * Who hears that the work exists. The three words the studio chooses at the
 * assign moment: `assigned` (default) tells the player taking the job,
 * `everyone` tells every active player — for open work that names no single
 * hand — and `none` tells no one.
 */
export const NOTIFY_TARGETS = ['assigned', 'everyone', 'none'] as const
export type NotifyTarget = (typeof NOTIFY_TARGETS)[number]

export const createAssignmentSchema = v.object({
  playerId: idText,
  paymentAmount: v.optional(moneyOrNull),
  paymentStatus: v.optional(v.picklist(PAYMENT_STATUSES)),
  estimatedHours: v.optional(hoursOrNull),
  notes: v.optional(nullableText(2000)),
  notify: v.optional(v.picklist(NOTIFY_TARGETS)),
})

export type CreateAssignmentInput = v.InferOutput<typeof createAssignmentSchema>

const assignmentDefaultsFields = {
  paymentAmount: v.optional(moneyOrNull),
  estimatedHours: v.optional(hoursOrNull),
  notes: v.optional(nullableText(2000)),
}

const uniquePlayers = (rows: { playerId: string }[]) => new Set(rows.map((row) => row.playerId)).size === rows.length
const assignmentList = v.pipe(v.array(createAssignmentSchema), v.minLength(1), v.maxLength(50),
  v.check(uniquePlayers, 'Choose each player only once.'))

export const createJobSchema = v.object({ ...JOB_FIELDS, publish: v.optional(v.boolean()), assignments: v.optional(assignmentList) })
export type CreateJobInput = v.InferOutput<typeof createJobSchema>

export const bulkAssignmentSchema = v.object({
  assignments: assignmentList,
  notify: v.optional(v.picklist(NOTIFY_TARGETS)),
})
export type BulkAssignmentInput = v.InferOutput<typeof bulkAssignmentSchema>

const assignmentDefaultsSchema = v.object(assignmentDefaultsFields)
export type AssignmentDefaults = v.InferOutput<typeof assignmentDefaultsSchema>

export const playerGroupSchema = v.object({
  name: required(80),
  players: v.pipe(v.array(v.object({ playerId: idText })), v.minLength(2), v.maxLength(50),
    v.check(uniquePlayers, 'Choose each player only once.')),
})
export type PlayerGroupInput = v.InferOutput<typeof playerGroupSchema>
export interface PlayerGroup extends PlayerGroupInput { id: string }
export interface AssignmentPlayer { id: string; discordName: string | null; email: string; availability: 'active' | 'inactive' }

/** Completion is not here; an assignment's state moves are its own endpoint. */
export const updateAssignmentSchema = v.pipe(
  v.object({
    paymentAmount: v.optional(moneyOrNull),
    paymentStatus: v.optional(v.picklist(PAYMENT_STATUSES)),
    paymentDate: v.optional(dateOrNull),
    estimatedHours: v.optional(hoursOrNull),
    actualHours: v.optional(hoursOrNull),
    quality: v.optional(v.union([v.pipe(v.number(), v.integer(), v.minValue(1), v.maxValue(5)), v.null()])),
    qualityNotes: v.optional(nullableText(2000)),
    notes: v.optional(nullableText(2000)),
  }),
  v.check((input) => Object.values(input).some((value) => value !== undefined), 'Nothing to change.'),
)

export type UpdateAssignmentInput = v.InferOutput<typeof updateAssignmentSchema>

/** A reviewed deduction changes credited work, never the allocation. */
export const deductAssignmentHoursSchema = v.object({
  hours: v.pipe(v.number(), v.minValue(0.25), v.maxValue(10_000)),
  reason: required(1000),
  evidenceUrl: v.optional(v.pipe(v.string(), v.url(), v.maxLength(2000), v.check((value) => /^https?:\/\//i.test(value), 'Use an HTTP or HTTPS evidence link.'))),
})
export type DeductAssignmentHoursInput = v.InferOutput<typeof deductAssignmentHoursSchema>

export interface AssignmentHourAdjustment {
  id: string
  assignmentId: string
  from: number
  to: number
  reason: string
  evidenceUrl: string | null
  actorName: string | null
  createdAt: string
}

/** A drop carries its reason; nothing else does. */
export const setAssignmentCompletionSchema = v.pipe(
  v.object({
    completion: v.picklist(ASSIGNMENT_COMPLETIONS),
    droppedReason: v.optional(nullableText(500)),
  }),
  v.check(
    (input) => input.completion !== 'dropped' || (typeof input.droppedReason === 'string' && input.droppedReason.trim() !== ''),
    'Say why the work was dropped.',
  ),
)

export type SetAssignmentCompletionInput = v.InferOutput<typeof setAssignmentCompletionSchema>

// ---- The portal's verbs ----------------------------------------------------------------
//
// The player's own: hours logged against their allocation as they go, the
// part they finish themselves, and a note to the studio on either. Each operates on their own assignment and nothing
// else — the server reads the viewer, never a player id from the body.

/** One stretch of work, in hours. Logged on top of what came before. */
export const logPortalHoursSchema = v.object({
  hours: v.pipe(
    v.number(),
    v.minValue(0.25, 'A stretch is at least a quarter of an hour.'),
    v.maxValue(24, 'A stretch that long is more than a day — log it in pieces.'),
  ),
})

export type LogPortalHoursInput = v.InferOutput<typeof logPortalHoursSchema>

/** The player's counter saves a total, with the last seen total guarding stale tabs. */
export const setPortalHoursSchema = v.object({
  hours: v.pipe(v.number(), v.minValue(0), v.maxValue(10_000)),
  expectedHours: v.pipe(v.number(), v.minValue(0), v.maxValue(10_000)),
})

export type SetPortalHoursInput = v.InferOutput<typeof setPortalHoursSchema>

/**
 * How a player hands their part in. `assigned`: the whole allocation was
 * worked, so the hours still unlogged are logged with the finish. `logged`:
 * only the hours on the counter were worked, so the part is handed in short
 * of its allocation and the studio sees that it is.
 */
export const COMPLETE_HOURS = ['assigned', 'logged'] as const
export type CompleteHours = (typeof COMPLETE_HOURS)[number]
export const completePortalAssignmentSchema = v.object({
  hours: v.optional(v.picklist(COMPLETE_HOURS), 'logged'),
})

export type CompletePortalAssignmentInput = v.InferOutput<typeof completePortalAssignmentSchema>

/** The player's word to the studio on their own assignment — always shared. */
export const portalNoteSchema = v.object({
  body: required(4000),
})

export type PortalNoteInput = v.InferOutput<typeof portalNoteSchema>

export const createNoteSchema = v.object({
  entityType: v.picklist(NOTE_ENTITY_TYPES),
  entityId: idText,
  body: required(4000),
  visibility: v.optional(v.picklist(NOTE_VISIBILITIES)),
})

export type CreateNoteInput = v.InferOutput<typeof createNoteSchema>

export const updateNoteSchema = v.pipe(
  v.object({
    body: v.optional(required(4000)),
    visibility: v.optional(v.picklist(NOTE_VISIBILITIES)),
  }),
  v.check((input) => Object.values(input).some((value) => value !== undefined), 'Nothing to change.'),
)

export type UpdateNoteInput = v.InferOutput<typeof updateNoteSchema>
